import React, {useEffect} from 'react';
import {Cell, TableInstance} from 'react-table';
import styled from 'styled-components';

import {HandlerOf, PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {alphaWhite} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useScrollableTable} from './hooks/useScrollableTable';
import {TableHeaderRow} from './tableHeaderRow';
import {TablePagination} from './tablePagination/tablePagination';
import {TableRow} from './tableRow';

/*
 * Props.
 */

export interface TableContentProps<T extends object> {
  tableInstance: TableInstance<T>;
  isLoading?: boolean;
  hiddenColumnIds?: string[];
  isFirstColumnSticky?: boolean;
  onCellSelect?: HandlerOf<Cell<T>>;
  isCellSelectable?: PredicateOf<Cell<T>>;
}

/*
 * Style.
 */

const StyledRelativeWrapper = styled.div`
  position: relative;
`;

const StyledTableWrapper = styled.div`
  overflow-x: auto;
  font-size: ${fontSizes.verySmall};
`;

const StyledTable = styled.table`
  border-spacing: 0;
  min-width: 100%;
`;

const StyledTableHeader = styled.thead`
  color: ${(p) => p.theme.greys.shade70};
  text-align: left;
`;

const StyledTableBody = styled.tbody`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledOverflowShadow = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 48px;
  /* Using alphaWhite(0) instead of 'alphas.transparent' as it's not supported by Safari for gradients
  https://stackoverflow.com/questions/38391457/linear-gradient-to-transparent-bug-in-latest-safari */
  background: linear-gradient(270deg, ${(p) => p.theme.foreground} 0%, ${alphaWhite(0)} 100%);
`;

/**
 * Component.
 * This component takes care of rendering the table and its interactions.
 */

export const TableContent = <T extends object>({
  isLoading,
  isFirstColumnSticky,
  hiddenColumnIds,
  onCellSelect,
  isCellSelectable,
  tableInstance,
}: TableContentProps<T>) => {
  const [tableContainerRef, tableRef, hasOverflow, hasScrolled, hasReachedScrollEnd] =
    useScrollableTable(isFirstColumnSticky);

  const {getTableProps, getTableBodyProps, headerGroups, page, prepareRow, setHiddenColumns} = tableInstance;
  // To my future self, selected rows can be found under tableInstance.state.selectedRowIds & tableInstance.selectedFlatRows (for full objects).

  useEffect(() => {
    if (hiddenColumnIds) {
      setHiddenColumns(hiddenColumnIds);
    }
  }, [hiddenColumnIds, setHiddenColumns]);

  return (
    <>
      <StyledRelativeWrapper>
        <StyledTableWrapper ref={tableContainerRef}>
          <StyledTable {...getTableProps()} ref={tableRef}>
            <StyledTableHeader>
              {
                // Loop over the header rows
                headerGroups.map((headerGroup, i) => (
                  <TableHeaderRow
                    // No other key possible than i here
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    isLoading={isLoading}
                    headerGroup={headerGroup}
                    isFirstColumnSticky={isFirstColumnSticky}
                    hasScrolled={hasScrolled}
                    hasOverflow={hasOverflow}
                  />
                ))
              }
            </StyledTableHeader>
            {/* Apply the table body props */}
            <StyledTableBody {...getTableBodyProps()}>
              {
                // Loop over the table rows
                page.map((row) => (
                  <TableRow
                    key={row.id}
                    prepareRow={prepareRow}
                    row={row}
                    isLoading={isLoading}
                    isFirstColumnSticky={isFirstColumnSticky}
                    hasScrolled={hasScrolled}
                    hasOverflow={hasOverflow}
                    onCellSelect={onCellSelect}
                    isCellSelectable={isCellSelectable}
                  />
                ))
              }
            </StyledTableBody>
          </StyledTable>
        </StyledTableWrapper>
        {maybeDisplayOverflowShadow(hasOverflow, hasReachedScrollEnd)}
      </StyledRelativeWrapper>
      <TablePagination<T> instance={tableInstance} isLoading={isLoading} />
    </>
  );
};

/*
 * Helpers.
 */

function maybeDisplayOverflowShadow(hasOverflow?: boolean, hasReachedScrollEnd?: boolean) {
  if (!hasOverflow || hasReachedScrollEnd) {
    return null;
  }
  return <StyledOverflowShadow />;
}
